/* eslint-disable no-shadow */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { Provider, connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  GoogleMap, withGoogleMap, withScriptjs, InfoWindow,
} from 'react-google-maps';
import { MarkerWithLabel } from 'react-google-maps/lib/components/addons/MarkerWithLabel';
import { MAP } from 'react-google-maps/lib/constants';
import haversine from 'haversine';
import {
  Button,
  getTranslation,
  Icon,
  ListIcon,
  LocationServicesIcon,
  TranslationProvider,
} from '@resy/ui-kitchen';

import './MapContainer.scss';
import MarkerIcon from '../../svg/marker-red.svg';
import MapStyles from './MapStyles.json';
import NoResults from '../../components/NoResults';
import Popover from '../../components/Popover';
import { getMarketLocationThunk, setSelectedLocationThunk } from '../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import { LIST_ID } from '../../../../../ui-kitchen/src/components/SearchFilterContainer/searchFilterConstants';
import SearchFilterContainer from '../../../../../ui-kitchen/src/components/SearchFilterContainer/SearchFilterContainer';

const FACET_TYPE = { list: LIST_ID };

class DeprecatedMapContainer extends React.Component {
  defaultOptions = {
    clickableIcons: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    mapTypeControl: false,
    maxZoom: 18,
    streetViewControl: false,
    styles: MapStyles,
    zoom: 12,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_BOTTOM,
    },
  }

  infoBoxOptions = {
    alignBottom: true,
    closeBoxURL: '',
    shouldFocus: true,
    enableEventPropagation: true,
    headerDisabled: true,
    infoBoxClearance: {
      height: 50,
      width: 50,
    },
    pixelOffset: {
      height: -60,
    },
  }

  markerSizeDefault = 40;

  markerSizeHover = this.markerSizeDefault * 1.5;

  preventGeolocationActiveChange = false;

  static propTypes = {
    $rootScope: PropTypes.shape({
      reduxStore: PropTypes.shape({}).isRequired,
    }).isRequired,
    $window: PropTypes.shape({}).isRequired,
    Analytics: PropTypes.shape({
      event: PropTypes.func.isRequired,
    }).isRequired,
    cuisines: PropTypes.arrayOf(PropTypes.object),
    defaultCenter: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }).isRequired,
    error: PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
      shouldHide: PropTypes.bool,
    }),
    getMarketLocationThunk: PropTypes.func.isRequired,
    hasGeolocationControlBeenClicked: PropTypes.bool.isRequired,
    hits: PropTypes.arrayOf(PropTypes.object).isRequired,
    hoveredVenue: PropTypes.number,
    isAuthenticated: PropTypes.bool,
    isActive: PropTypes.bool.isRequired,
    isGeolocationActive: PropTypes.bool.isRequired,
    isGeolocationBlocked: PropTypes.bool.isRequired,
    isGeolocationLoading: PropTypes.bool.isRequired,
    isGeolocationSupported: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool,
    isSearching: PropTypes.bool.isRequired,
    listCount: PropTypes.number,
    lists: PropTypes.arrayOf(PropTypes.object),
    nearbyTranslation: PropTypes.string.isRequired,
    onActiveViewToggle: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onExpandToVenues: PropTypes.func.isRequired,
    onExpandMap: PropTypes.func.isRequired,
    onGeolocationActiveChange: PropTypes.func.isRequired,
    onGeolocationClick: PropTypes.func.isRequired,
    onInfoBoxSelect: PropTypes.func.isRequired,
    onMapChange: PropTypes.func.isRequired,
    onMarkerSelect: PropTypes.func.isRequired,
    onRedo: PropTypes.func.isRequired,
    query: PropTypes.shape({
      type: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    seating: PropTypes.arrayOf(PropTypes.object),
    selectedDate: PropTypes.string,
    selectedLocation: PropTypes.shape({
      code: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      map_center: PropTypes.shape({
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
      }),
      name: PropTypes.string.isRequired,
      radius: PropTypes.number.isRequired,
    }).isRequired,
    selectedPartySize: PropTypes.number,
    selectedTime: PropTypes.string,
    selectedVenueId: PropTypes.number,
    setSelectedLocationThunk: PropTypes.func.isRequired,
    shouldCenterOnUser: PropTypes.bool.isRequired,
    shouldFitBounds: PropTypes.bool.isRequired,
    shouldShowRedo: PropTypes.bool.isRequired,
    urlSlug: PropTypes.string.isRequired,
    userLocation: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
    }),
  }

  static defaultProps = {
    cuisines: undefined,
    error: null,
    hoveredVenue: null,
    isAuthenticated: false,
    isMobile: true,
    listCount: undefined,
    lists: undefined,
    query: null,
    seating: undefined,
    selectedDate: null,
    selectedPartySize: null,
    selectedTime: undefined,
    selectedVenueId: null,
    userLocation: null,
  }

  componentDidMount() {
    const {
      hits,
      shouldFitBounds,
    } = this.props;

    if (hits.length > 0 && shouldFitBounds) {
      this.fitBounds();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      hits,
      isActive,
      shouldCenterOnUser,
      shouldFitBounds,
      userLocation,
      query,
    } = this.props;
    if ((hits.length > 0 && JSON.stringify(hits) !== JSON.stringify(prevProps.hits)) || (isActive && isActive !== prevProps.isActive && hits.length > 0)) {
      // NOTE: catching max zoom to focus on markers
      if (shouldFitBounds || this.map.getZoom() === 1 || (query?.type === 'query' && query?.value === '')) {
        this.fitBounds();
      }

      this.renderMarkers();
    }

    if (shouldCenterOnUser && shouldCenterOnUser !== prevProps.shouldCenterOnUser) {
      this.map.panTo(userLocation);
    }
  }

  /**
   * 80 meters was chosen because a quick read indicates this is the smaller "standard" for a city block
   * Reference: https://en.wikipedia.org/wiki/City_block#Grid_plan
   */
  didCoordinatesChange = (start, end, threshold, unit) => !haversine(start, end, { threshold: (threshold || 80), unit: (unit || 'meter') });

  fitBounds = () => {
    const { hits } = this.props;
    const bounds = new google.maps.LatLngBounds();
    hits.forEach((hit) => bounds.extend(hit._geoloc)); // eslint-disable-line no-underscore-dangle
    this.map.fitBounds(bounds);
  }

  isWithinLocationRadius = (coordinates) => {
    const { selectedLocation } = this.props;
    const { latitude, longitude } = selectedLocation.map_center;
    const selectedLocationCenter = { latitude, longitude };
    return !this.didCoordinatesChange(selectedLocationCenter, coordinates, selectedLocation.radius, 'mile');
  }

  onGeolocationClick = () => {
    const {
      $rootScope,
      $window,
      getMarketLocationThunk,
      hasGeolocationControlBeenClicked,
      isGeolocationBlocked,
      nearbyTranslation,
      selectedLocation,
      setSelectedLocationThunk,
      userLocation,
    } = this.props;

    if (hasGeolocationControlBeenClicked) {
      const { onGeolocationActiveChange } = this.props;

      if (!isGeolocationBlocked) {
        this.preventGeolocationActiveChange = true;
        onGeolocationActiveChange();
      }

      if (userLocation) {
        const coordinates = {
          latitude: userLocation.lat,
          longitude: userLocation.lng,
        };
        if (this.isWithinLocationRadius(coordinates)) {
          this.map.panTo(userLocation);
        } else {
          getMarketLocationThunk({ coordinates: userLocation })
            .then(
              (location) => {
                const params = { $rootScope, $window };
                if (location) {
                  params.location = location;
                  setSelectedLocationThunk(params);
                } else {
                  params.location = { ...selectedLocation, name: nearbyTranslation, translationKey: 'nearby' };
                  setSelectedLocationThunk(params);
                  this.map.panTo(userLocation);
                }
              },
              () => { }
            );
        }
      }
    } else {
      const { onGeolocationClick } = this.props;
      onGeolocationClick();
    }
  }

  onIdle = () => {
    if (this.map) {
      const { onMapChange } = this.props;
      onMapChange(this.map.getBounds(), this.map.getZoom(), this.preventGeolocationActiveChange, false);
    }
    this.preventGeolocationActiveChange = false;
  }

  onMapClick = () => {
    const { onMarkerSelect } = this.props;
    onMarkerSelect(null);
  }

  onMarkerClick = (venue) => {
    const { onMarkerSelect } = this.props;

    onMarkerSelect(venue);
    window.addEventListener('keydown', this.escKeyListener);
  }

  escKeyListener = (event) => {
    const { onMarkerSelect } = this.props;

    if (event.key === 'Escape') {
      onMarkerSelect(null);
      window.removeEventListener('keydown', this.escKeyListener);
    }
  }

  renderFilters = () => {
    const {
      $rootScope,
      Analytics,
      cuisines,
      isAuthenticated,
      isMobile,
      lists,
      seating,
      urlSlug,
    } = this.props;
    return (
      <div className="DeprecatedMapContainer__filters">
        <SearchFilterContainer
          $rootScope={$rootScope}
          Analytics={Analytics}
          cuisines={cuisines}
          isAuthenticated={isAuthenticated}
          isMobile={isMobile}
          lists={lists}
          seating={seating}
          urlSlug={urlSlug}
        />
      </div>
    );
  };

  renderActiveViewToggle = () => {
    const { isMobile, onActiveViewToggle } = this.props;
    if (isMobile) {
      return (
        <Button
          className="ActiveViewToggle"
          type="button"
          onClick={onActiveViewToggle}
        >
          <Icon component={ListIcon} name="list" />
          <TranslationProvider translationKey="list">
            List
          </TranslationProvider>
        </Button>
      );
    }
    return null;
  }

  renderError = () => {
    const { error } = this.props;
    if (error) {
      return (
        <div
          className={cx('Error__container', {
            'Error__container--hide': error.shouldHide,
          })}
        >
          <div className="Error">
            {error.message || 'Something went wrong. Please try again.'}
          </div>
        </div>
      );
    }
    return null;
  }

  renderGeolocationControl = () => {
    const {
      isGeolocationActive,
      isGeolocationBlocked,
      isGeolocationLoading,
      isGeolocationSupported,
    } = this.props;

    if (isGeolocationSupported) {
      return (
        <button
          className={cx('GeolocationControl', {
            'GeolocationControl--active': isGeolocationActive,
            'GeolocationControl--blocked': isGeolocationBlocked,
            'GeolocationControl--loading': isGeolocationLoading,
          })}
          type="button"
          tabIndex={0}
          aria-label={getTranslation({
            translationKey: 'current_location',
            fallback: 'Current Location',
          })}
          onClick={this.onGeolocationClick}
        >
          <Icon component={LocationServicesIcon} name="LocationServices" />
        </button>
      );
    }
    return null;
  }

  renderMarkers = () => {
    const {
      $rootScope,
      hits,
      isMobile,
      hoveredVenue,
      onInfoBoxSelect,
      selectedDate,
      selectedPartySize,
      selectedTime,
      selectedVenueId,
    } = this.props;

    return hits.map((hit) => (
      <MarkerWithLabel
        key={hit.id}
        aria-label={hit.name}
        title={hit.name}
        labelAnchor={{ x: 20, y: 40 }}
        labelClass={cx('MarkerIcon__container', {
          'MarkerIcon__container--active': hoveredVenue === hit.id || selectedVenueId === hit.id,
        })}
        onClick={() => this.onMarkerClick(hit)}
        opacity={0}
        position={hit._geoloc} // eslint-disable-line no-underscore-dangle
        tabIndex={0}
      >
        <>
          <MarkerIcon
            className={cx('MarkerIcon', {
              'MarkerIcon--rga': !!hit.isGda,
              'MarkerIcon--active': hoveredVenue === hit.id || selectedVenueId === hit.id,
            })}
          />
          {selectedVenueId === hit.id && (
            <InfoWindow
              defaultPosition={new google.maps.LatLng(hit._geoloc.lat, hit._geoloc.lng)} // eslint-disable-line no-underscore-dangle
              options={this.infoBoxOptions}
            >
              <div
                onClick={() => onInfoBoxSelect(hit)}
                onKeyPress={() => onInfoBoxSelect(hit)}
              >
                <Provider store={$rootScope.reduxStore}>
                  <Popover
                    isMobile={isMobile}
                    selectedDate={selectedDate}
                    selectedPartySize={selectedPartySize}
                    selectedTime={selectedTime}
                    venue={hit}
                  />
                </Provider>
                <div className="InfoBox__pointer" />
              </div>
            </InfoWindow>
          )}
        </>
      </MarkerWithLabel>
    ));
  }

  renderNoResults = () => {
    const {
      hits,
      isMobile,
      isSearching,
      onClear,
      query,
    } = this.props;
    if (isMobile && !isSearching && hits.length === 0) {
      return <NoResults onClear={onClear} query={query} />;
    }
    return null;
  }

  renderRedo = () => {
    const {
      isMobile,
      onRedo,
      shouldShowRedo,
      onMapChange,
    } = this.props;

    if (shouldShowRedo) {
      return (
        <TranslationProvider translationKey="map_redo">
          <button
            className={cx('update-map', {
              'update-map-mobile': isMobile,
            })}
            onClick={() => {
              onRedo(true);
              if (onMapChange) {
                onMapChange(this.map.getBounds(), this.map.getZoom(), this.preventGeolocationActiveChange, true);
              }
            }}
            type="button"
          >
            Search Here
          </button>
        </TranslationProvider>
      );
    }
    return null;
  }

  renderExpandButton = () => {
    const {
      hits,
      isMobile,
      listCount,
      onExpandMap,
      onExpandToVenues,
      shouldShowRedo,
    } = this.props;

    const { search } = window.location;
    const isListFilter = (search.indexOf(FACET_TYPE.list) > -1);
    if (!shouldShowRedo && isListFilter && hits.length < listCount && this.map) {
      return (
        <TranslationProvider translationKey="show_all_venues">
          <Button
            className={cx('update-map', {
              'update-map-mobile': isMobile,
            })}
            onClick={() => onExpandToVenues(this.map)}
          >
            Show Full List
          </Button>
        </TranslationProvider>
      );
    }

    if (!shouldShowRedo && hits.length <= 5 && this.map) {
      return (
        <TranslationProvider translationKey="expand_nearby">
          <Button
            className={cx('update-map', {
              'update-map-mobile': isMobile,
            })}
            onClick={() => onExpandMap(this.map)}
          >
            Expand Nearby
          </Button>
        </TranslationProvider>
      );
    }
    return null;
  }

  renderUserLocation = () => {
    const {
      // isMobile,
      userLocation,
    } = this.props;

    if (userLocation) {
      return (
        <MarkerWithLabel
          labelAnchor={{ x: 10, y: 10 }} // 10 is half the UserLocationMarker size (20)
          labelClass="UserLocationMarker__container"
          opacity={0}
          position={userLocation}
        >
          <div className="UserLocationMarker__outer-circle">
            <div className="UserLocationMarker">
              <div
                className={cx('UserLocationMarker__inner-circle', {
                  // if we want to implement watchPosition and follow the user (mobile)
                  // pulsate: isMobile,
                })}
              />
            </div>
          </div>
        </MarkerWithLabel>
      );
    }
    return null;
  }

  setMapRef = (map) => {
    if (map && !this.map) {
      this.map = map.context[MAP];
    }
  }

  render() {
    const {
      defaultCenter,
      hits,
      isLoading,
      isMobile,
      isSearching,
    } = this.props;

    if (isLoading || (hits.length === 0 && isSearching)) {
      return (
        <GoogleMap
          defaultCenter={defaultCenter}
          defaultOptions={this.defaultOptions}
        />
      );
    }

    return (
      <GoogleMap
        defaultCenter={defaultCenter}
        defaultOptions={this.defaultOptions}
        onClick={this.onMapClick}
        onIdle={this.onIdle}
        ref={this.setMapRef}
      >
        {isMobile && this.renderFilters()}
        {this.renderActiveViewToggle()}
        {this.renderRedo()}
        {this.renderExpandButton()}
        {this.renderGeolocationControl()}
        {this.renderMarkers()}
        {this.renderNoResults()}
        {this.renderUserLocation()}
        {this.renderError()}
      </GoogleMap>
    );
  }
}

DeprecatedMapContainer.displayName = 'DeprecatedMapContainer';

const mapStateToProps = ({ favorite: { lists } }) => {
  const { search } = window.location;
  let listCount;
  const isListFilter = (search.indexOf(FACET_TYPE.list) > -1);
  if (isListFilter) {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const facet = params.get('facet');
    const [, listId] = facet.split(':');
    const listById = lists.filter((list) => (list.listId === listId));
    listCount = listById[0]?.count;
  }
  return { listCount };
};

const mapDispatchToProps = {
  getMarketLocationThunk, setSelectedLocationThunk,
};
const WrappedDeprecatedMapContainer = withScriptjs(withGoogleMap(DeprecatedMapContainer));

export { DeprecatedMapContainer as DeprecatedMapContainerForTesting };
export default connect(mapStateToProps, mapDispatchToProps)(WrappedDeprecatedMapContainer);
